import type { ProblemDetails } from "types/errors";

import { parseJsonObject } from "./parsers";
import { captureException } from "@sentry/react";
import { type ScopeContext } from "@sentry/core";

export class ResponseError extends Error {
  constructor(
    message: string,
    response: Response,
    body?: ProblemDetails,
    cause?: Error,
  ) {
    super(message, cause);

    this.name = "ResponseError";
    this.status = response.status;
    this.statusText = response.statusText;
    this.traceId =
      body?.traceId ?? response.headers.get("X-Trace-Id") ?? undefined;
    this.title = body?.title;
    this.detail = body?.detail;
    this.instance = body?.instance;
    this.typeUrl = body?.type;
    this.errors = body?.errors;
  }

  status: number;
  statusText: string;
  traceId?: string;
  title?: string;
  detail?: string;
  instance?: string;
  typeUrl?: string;
  errors?: Record<string, Array<string>>;
  cause?: Error;
}

export const throwResponseErrorAsync = async (
  errorMessage: string,
  response: Response,
) => {
  let body: ProblemDetails | undefined;
  let error: Error | undefined;
  const responseText = await response.text();

  try {
    body = parseJsonObject<ProblemDetails>(responseText);
  } catch (err) {
    error = err as Error;
  }

  throw new ResponseError(errorMessage, response, body, error);
};

export const captureResponseError = (
  error: Error | ResponseError,
  scopeContext?: Partial<ScopeContext>,
) => {
  const isResponseError = error instanceof ResponseError;

  const hint = {
    ...scopeContext,
  } satisfies Partial<ScopeContext>;

  if (isResponseError) {
    const tags = {
      ...scopeContext?.tags,
    };

    const responseErrorTags = ["status", "statusText", "traceId"] as const;

    for (const tag of responseErrorTags) {
      const value = error[tag];

      if (value !== undefined) {
        tags[tag] = value;
      }
    }

    hint.tags = tags;

    const contextKey = scopeContext?.contexts?.response_error
      ? `response_error_${String(hint.tags?.component ?? hint.tags?.caller ?? "unknown")}`
      : "response_error";

    hint.contexts = {
      ...scopeContext?.contexts,
      [contextKey]: {
        name: error.name,
        message: error.message,
        cause: error.cause,
        title: error.title,
        detail: error.detail,
        instance: error.instance,
        typeUrl: error.typeUrl,
        errors: error.errors,
      },
    };
  }

  captureException(error, hint);
};
